import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaMinus, FaPlus } from 'react-icons/fa';
import { Link, LoaderFunction, json, useLoaderData, useNavigate } from 'react-router-dom';

import { get, ref, update } from 'firebase/database';
import { db } from '../../../firebase';

import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';

import AmountInput from '../../../components/layout/AmountInput';
import Content from '../../../components/layout/Content';
import DeleteConfirmModal from '../../../components/layout/modals/DeleteConfirmModal';

import classes from './Edit.module.css';

type LoaderData = {
  id: string;
  name: string;
  score: number;
};

const Edit = () => {
  const [amount, setAmount] = useState<number | string>('');
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const data = useLoaderData() as LoaderData;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const amountChangeHandler = async (direction: string) => {
    if (amount === '') {
      return;
    }
    const realAmount = amount as number;

    let newScore = data.score;
    if (direction === 'add') {
      newScore += realAmount;
    }
    if (direction === 'subtract') {
      if (newScore - realAmount < 0) {
        newScore = 0;
      } else {
        newScore -= realAmount;
      }
    }
    await update(ref(db, `consumptions/${data.id}`), { score: newScore });
    navigate('/afterwork');
  };

  const inputChangeHandler = (amount: number) => {
    if (amount > 0) {
      setAmount(amount);
    } else {
      setAmount('');
    }
  };

  const deleteGroupHandler = () => {
    setShowDeleteConfirmModal(true);
  };

  const deleteGroupCancelHandler = () => {
    setShowDeleteConfirmModal(false);
  };

  const deleteGroupConfirmHandler = async () => {
    await update(ref(db, `consumptions/`), { [data.id]: null });
    navigate('/afterwork');
  };

  return (
    <Content>
      {showDeleteConfirmModal && (
        <DeleteConfirmModal
          groupName={data.name}
          onCancel={deleteGroupCancelHandler}
          onConfirm={deleteGroupConfirmHandler}
        />
      )}
      <Card extraStyles={{ flex: 0.1 }} withMargin="bottom">
        <h1 style={{ fontSize: 'min(2em, 6vw)' }}>{data.name}</h1>
      </Card>
      <Card extraStyles={{ justifyContent: 'flex-start' }}>
        <p className={classes.score}>
          {t('editPage.score')}: {data.score}&#127866;
        </p>
        <p className={classes.label}>{t('editPage.amount')}</p>
        <AmountInput onChange={inputChangeHandler} value={amount} />
        <div className={classes.actionContainer}>
          <Button onClick={amountChangeHandler.bind(null, 'add')} disabled={amount === ''}>
            <FaPlus />
          </Button>
          <Button onClick={amountChangeHandler.bind(null, 'subtract')} disabled={amount === ''}>
            <FaMinus />
          </Button>
        </div>
        <Button extraStyles={{ marginTop: 'min(8em, 12vh)', width: 'min(12em, 40vw)' }} onClick={deleteGroupHandler}>
          {t('editPage.delete')}
        </Button>
      </Card>
      <Card extraStyles={{ flex: 0.1 }} withMargin="top">
        <Link to="..">
          <Button>{t('buttons.back')}</Button>
        </Link>
      </Card>
    </Content>
  );
};

export const loader: LoaderFunction = async ({ params }) => {
  const data = await get(ref(db, `consumptions/${params.groupId}`));
  if (!data.exists()) {
    return json({ id: null });
  }
  const group = data.val() as { name: string; score: number };
  return json({ id: params.groupId, ...group });
};

export default Edit;
